import type {PropertyDefinition} from '@github-ui/custom-properties-types'
import {ListItem, ListView} from '@github-ui/list-view'
import {customPropertyDetailsPath} from '@github-ui/paths'
import {Link} from '@github-ui/react-core/link'
import {TrashIcon} from '@primer/octicons-react'
import {ActionList, Box, Label, Text} from '@primer/react'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import {createRef, useState} from 'react'

import {useCurrentOrg} from '../contexts/CurrentOrgRepoContext'
import {definitionTypeLabels} from '../helpers/definition-type-labels'
import {DeleteDefinitionDialog} from './DeleteDefinitionDialog'
import {EmptyState} from './EmptyState'

interface Props {
  definitions: PropertyDefinition[]
}

export function DefinitionsList({definitions}: Props) {
  if (definitions.length === 0) {
    return <EmptyState>No properties have been added</EmptyState>
  }

  return (
    <Box
      data-hpc
      sx={{border: '1px solid', borderColor: 'border.muted', borderRadius: 2}}
      data-testid="repos-definitions-list"
    >
      <ListView
        title="Property definitions"
        variant="compact"
        metadata={
          <ListView.Metadata
            title={
              <Text sx={{fontWeight: 'bold'}}>
                {definitions.length} {definitions.length === 1 ? 'property' : 'properties'}
              </Text>
            }
          />
        }
      >
        {definitions.map(definition => (
          <DefinitionListItem key={definition.propertyName} definition={definition} />
        ))}
      </ListView>
    </Box>
  )
}

interface DefinitionListItemProps {
  definition: PropertyDefinition
}

function DefinitionListItem({definition}: DefinitionListItemProps) {
  const [deleteDialogIsOpen, setDeleteDialogIsOpen] = useState(false)
  const org = useCurrentOrg()

  const contextMenuButtonRef = createRef<HTMLButtonElement>()

  function closeDeleteDialog() {
    setDeleteDialogIsOpen(false)
    contextMenuButtonRef.current?.focus()
  }

  const unfurlOnSmallScreen: BetterSystemStyleObject = {
    gridTemplateRows: ['repeat(2, auto)', 'auto'],
    gridTemplateAreas: [
      '"primary actions" "metadata metadata" "main-content main-content"',
      '"primary main-content metadata actions"',
    ],
    gridTemplateColumns: [
      'auto minmax(0, 3em)',
      'minmax(0, max-content) auto minmax(0, min-content) minmax(0, min-content)',
    ],
    pl: 2,
  }

  return (
    <>
      <ListItem
        sx={{...unfurlOnSmallScreen, gap: 0}}
        title={
          <ListItem.Title
            value={definition.propertyName}
            containerSx={{
              display: 'flex',
              flexFlow: 'row',
              alignItems: 'baseline',
              ml: 2,
            }}
            href="#"
            linkProps={{
              as: Link,
              to: customPropertyDetailsPath({org: org.login, propertyName: definition.propertyName}),
            }}
          >
            {definition.required && (
              <Label variant="secondary" sx={{ml: 2}}>
                Required
              </Label>
            )}
          </ListItem.Title>
        }
        metadata={
          <ListItem.Metadata variant="secondary" alignment="right" sx={{pl: 1, width: 'unset'}}>
            <Text sx={{display: ['none', 'flex'], whiteSpace: 'nowrap'}}>
              {definitionTypeLabels[definition.valueType] || ''}
            </Text>
          </ListItem.Metadata>
        }
        metadataContainerSx={{pr: 0}}
        secondaryActions={
          <ListItem.ActionBar
            sx={{pr: 1, minWidth: 0}}
            anchorRef={contextMenuButtonRef}
            staticMenuActions={[
              {
                key: 'delete',
                render: () => (
                  <ActionList.Item variant="danger" onSelect={() => setDeleteDialogIsOpen(true)}>
                    <ActionList.LeadingVisual>
                      <TrashIcon />
                    </ActionList.LeadingVisual>
                    Delete
                  </ActionList.Item>
                ),
              },
            ]}
          />
        }
      >
        <ListItem.MainContent>
          {definition.description && (
            <ListItem.Description
              sx={{
                minWidth: 0,
                maxWidth: '100%',
                display: 'flex',
              }}
            >
              <ListItem.DescriptionItem
                sx={{
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  display: 'block',
                  ml: 2,
                  mb: [1, 0],
                }}
              >
                {definition.description}
              </ListItem.DescriptionItem>
            </ListItem.Description>
          )}
        </ListItem.MainContent>
      </ListItem>

      {deleteDialogIsOpen && (
        <DeleteDefinitionDialog definition={definition} onCancel={closeDeleteDialog} onDismiss={closeDeleteDialog} />
      )}
    </>
  )
}

try{ DefinitionsList.displayName ||= 'DefinitionsList' } catch {}
try{ DefinitionListItem.displayName ||= 'DefinitionListItem' } catch {}